"use client"
import React, { useState, Suspense } from 'react'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import { LogIn } from "lucide-react"
import { SubtleSpotlight } from "@/components/ui/subtle-spotlight"

function SignInForm() {
  const searchParams = useSearchParams()
  const [identifier, setIdentifier] = useState('')
  const [password, setPassword] = useState('')
  const [msg, setMsg] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setMsg(null)
    setLoading(true)
    try {
      const res = await fetch('/api/auth/signin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier, password })
      })
      const json = await res.json()
      if (!res.ok) setMsg(json.error || 'Sign in failed')
      else {
        // Redirect to the 'next' parameter if it exists, otherwise go to /forms
        // Ensure the redirect URL is relative to prevent open redirect vulnerabilities
        let nextUrl = searchParams.get('next') || '/forms'
        // Only allow relative URLs (starting with /)
        if (!nextUrl.startsWith('/')) {
          nextUrl = '/forms'
        }
        window.location.href = nextUrl
      }
    } catch (err) {
      setMsg('Something went wrong')
    } finally {
      setLoading(false)
    }
  }

  return (
    <SubtleSpotlight color="rgba(0,0,0,0.04)">
      <Card className="rounded-none border bg-card shadow-sm transition-shadow hover:shadow-md">
        <CardHeader className="space-y-1 p-10 pb-2">
          <CardTitle className="text-2xl">Sign in</CardTitle>
          <CardDescription>Access your workspace</CardDescription>
        </CardHeader>
        <CardContent className="p-10 pt-2">
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="identifier" className="text-xs font-medium">Email or username</Label>
              <Input
                id="identifier"
                type="text"
                value={identifier}
                onChange={(e)=>setIdentifier(e.target.value)}
                placeholder="you@example.com or username"
                autoComplete="username"
                className="h-9 text-sm rounded-none"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-medium">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e)=>setPassword(e.target.value)} autoComplete="current-password" className="h-9 text-sm rounded-none" />
            </div>
            {msg && <p className="text-sm text-destructive">{msg}</p>}
            <Button type="submit" className="w-full text-sm rounded-none group" disabled={loading}>
              {loading ? 'Signing in…' : <>Sign in <LogIn className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" /></>}
            </Button>
            <p className="pt-2 text-center text-xs text-muted-foreground">
              Need an account?{" "}
              <Link href="/signup" className="font-medium text-primary underline-offset-4 hover:underline">
                Sign up
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </SubtleSpotlight>
  )
}

export default function SignInPage() {
  return (
    <main className="min-h-screen grid place-items-center bg-background" style={{ fontFamily: '"Segoe UI", SegoeUI, -apple-system, BlinkMacSystemFont, system-ui, Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji"' }}>
      <div className="w-full max-w-sm md:max-w-md px-6 py-12">
        <Suspense fallback={
          <SubtleSpotlight color="rgba(0,0,0,0.04)">
            <Card className="rounded-none border bg-card shadow-sm">
              <CardHeader className="space-y-1 p-10 pb-2">
                <CardTitle className="text-2xl">Sign in</CardTitle>
                <CardDescription>Access your workspace</CardDescription>
              </CardHeader>
              <CardContent className="p-10 pt-2">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-medium">Email or username</Label>
                    <Input disabled className="h-9 text-sm rounded-none" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-medium">Password</Label>
                    <Input disabled type="password" className="h-9 text-sm rounded-none" />
                  </div>
                  <Button disabled className="w-full text-sm rounded-none">Loading...</Button>
                </div>
              </CardContent>
            </Card>
          </SubtleSpotlight>
        }>
          <SignInForm />
        </Suspense>
      </div>
    </main>
  )
}
